import { useState, useEffect, useCallback } from "react";
import { UserMinus, UserPlus, Check, X, Clock, AlertTriangle } from "lucide-react";
import { VentureCard, VentureCardHeader, VentureCardTitle, VentureCardContent } from "@/components/ui/venture-card";
import { BadgeVenture } from "@/components/ui/badge-venture";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface CloserRequest {
  id: string;
  client_id: string;
  closer_id: string;
  request_type: string;
  status: string;
  reason: string | null;
  created_at: string;
  closers: { name: string } | null;
  clients: { company_name: string } | null;
}

export function CloserRequestsTab() {
  const [requests, setRequests] = useState<CloserRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState<CloserRequest | null>(null);
  const [actionType, setActionType] = useState<'approve' | 'dismiss' | null>(null);

  const fetchRequests = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('closer_requests')
        .select('*, closers(name), clients(company_name)')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching closer requests:', error);
        setRequests([]);
        return;
      }

      setRequests((data as CloserRequest[]) || []);
    } catch (err) {
      console.error('Error fetching closer requests:', err);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const updateRequestStatus = async (id: string, newStatus: 'approved' | 'dismissed') => {
    try {
      const request = requests.find(r => r.id === id);

      const { error } = await supabase
        .from('closer_requests')
        .update({
          status: newStatus,
          processed_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (error) {
        console.error('Error updating closer request:', error);
        toast.error("Failed to update request");
        return;
      }

      // If approved and we have a valid closer_id, actually remove the assignment
      if (newStatus === 'approved' && request?.closer_id) {
        const { error: assignError } = await supabase
          .from('closer_assignments')
          .update({ status: 'removed' })
          .eq('closer_id', request.closer_id)
          .eq('client_id', request.client_id);

        if (assignError) {
          console.error('Error removing closer assignment:', assignError);
          // Don't fail the whole operation — request is already approved
        }
      }

      // Update local state
      setRequests(prev =>
        prev.map(r => r.id === id ? { ...r, status: newStatus } : r)
      );

      toast.success(
        newStatus === 'approved'
          ? "Request approved. The closer has been removed from this client."
          : "Request dismissed."
      );
    } catch (err) {
      console.error('Error updating closer request:', err);
      toast.error("Failed to update request");
    } finally {
      setSelectedRequest(null);
      setActionType(null);
    }
  };

  const pendingRequests = requests.filter(r => r.status === 'pending');
  const processedRequests = requests.filter(r => r.status !== 'pending');

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <BadgeVenture variant="warning">Pending</BadgeVenture>;
      case 'approved':
        return <BadgeVenture variant="success">Approved</BadgeVenture>;
      case 'dismissed':
        return <BadgeVenture variant="error">Dismissed</BadgeVenture>;
      default:
        return <BadgeVenture variant="default">{status}</BadgeVenture>;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <VentureCard>
          <VentureCardContent>
            <div className="text-center py-12">
              <div className="animate-pulse space-y-4">
                <div className="h-4 bg-muted rounded w-1/3 mx-auto" />
                <div className="h-4 bg-muted rounded w-1/2 mx-auto" />
              </div>
            </div>
          </VentureCardContent>
        </VentureCard>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Pending Requests */}
      <VentureCard>
        <VentureCardHeader>
          <VentureCardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-warning" />
            Pending Requests
            {pendingRequests.length > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-warning/20 text-warning text-xs rounded-full">
                {pendingRequests.length}
              </span>
            )}
          </VentureCardTitle>
        </VentureCardHeader>
        <VentureCardContent>
          {pendingRequests.length === 0 ? (
            <div className="text-center py-8">
              <Check className="w-12 h-12 mx-auto text-success mb-4" />
              <p className="text-muted-foreground">No pending requests</p>
              <p className="text-sm text-muted-foreground">All closer requests have been processed.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {pendingRequests.map((request) => (
                <div
                  key={request.id}
                  className="p-4 rounded-lg border border-border hover:border-primary transition-colors"
                >
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          request.request_type === 'removal'
                            ? 'bg-destructive/20'
                            : 'bg-secondary/20'
                        }`}>
                          {request.request_type === 'removal'
                            ? <UserMinus className="w-5 h-5 text-destructive" />
                            : <UserPlus className="w-5 h-5 text-secondary" />
                          }
                        </div>
                        <div>
                          <p className="font-medium">{request.clients?.company_name || 'Unknown Client'}</p>
                          <p className="text-sm text-muted-foreground">
                            {request.request_type === 'removal' ? 'Removal' : 'Replacement'} request for{' '}
                            <strong>{request.closers?.name || 'Unknown Closer'}</strong>
                          </p>
                        </div>
                      </div>

                      {request.reason && (
                        <div className="mt-3 p-3 rounded-lg bg-muted/50 border border-border">
                          <p className="text-xs text-muted-foreground mb-1">Reason:</p>
                          <p className="text-sm">{request.reason}</p>
                        </div>
                      )}

                      <p className="text-xs text-muted-foreground">
                        Submitted: {new Date(request.created_at).toLocaleString()}
                      </p>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => {
                          setSelectedRequest(request);
                          setActionType('approve');
                        }}
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Approve
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedRequest(request);
                          setActionType('dismiss');
                        }}
                      >
                        <X className="w-4 h-4 mr-1" />
                        Dismiss
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </VentureCardContent>
      </VentureCard>

      {/* Processed Requests History */}
      {processedRequests.length > 0 && (
        <VentureCard>
          <VentureCardHeader>
            <VentureCardTitle>Request History</VentureCardTitle>
          </VentureCardHeader>
          <VentureCardContent>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Client</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Closer</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Type</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {processedRequests.map((request) => (
                    <tr key={request.id} className="border-b border-border">
                      <td className="py-3 px-4">{request.clients?.company_name || 'Unknown'}</td>
                      <td className="py-3 px-4">{request.closers?.name || 'Unknown'}</td>
                      <td className="py-3 px-4">
                        <BadgeVenture variant={request.request_type === 'removal' ? 'error' : 'info'}>
                          {request.request_type}
                        </BadgeVenture>
                      </td>
                      <td className="py-3 px-4">{getStatusBadge(request.status)}</td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">
                        {new Date(request.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </VentureCardContent>
        </VentureCard>
      )}

      {/* Confirmation Dialog */}
      <AlertDialog open={!!selectedRequest && !!actionType} onOpenChange={() => {
        setSelectedRequest(null);
        setActionType(null);
      }}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {actionType === 'approve' ? (
                <Check className="w-5 h-5 text-success" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-warning" />
              )}
              {actionType === 'approve' ? 'Approve Request' : 'Dismiss Request'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {actionType === 'approve' ? (
                <>
                  Approving this request will notify <strong>{selectedRequest?.clients?.company_name}</strong> and
                  initiate the {selectedRequest?.request_type} process for <strong>{selectedRequest?.closers?.name}</strong>.
                </>
              ) : (
                <>
                  Are you sure you want to dismiss this request from <strong>{selectedRequest?.clients?.company_name}</strong>?
                  The client will be notified that their request was not approved.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => selectedRequest && updateRequestStatus(
                selectedRequest.id,
                actionType === 'approve' ? 'approved' : 'dismissed'
              )}
              className={actionType === 'approve' ? '' : 'bg-muted text-foreground hover:bg-muted/80'}
            >
              {actionType === 'approve' ? 'Approve' : 'Dismiss'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
